/**
 * priorityPlusNav — Alpine.js component implementing the Priority+ navigation pattern.
 *
 * As the nav-wrap container narrows, <li> items are hidden from the right one by one.
 * The hamburger button becomes visible as soon as any item overflows. When the container
 * widens again, items are restored and the hamburger hides.
 *
 * Usage in HTML:
 *   <div class="nav-wrap" data-x-data="priorityPlusNav">
 *     <nav class="nav-default flex">
 *       <ul>...</ul>
 *     </nav>
 *     <button class="js-open-mobile-menu hidden">...</button>
 *   </div>
 *
 * Assumptions:
 *   - Nav items are direct <li> children of the first <ul> inside .nav-default.
 *   - The hamburger matches .js-open-mobile-menu and starts with class "hidden".
 *   - Item content and the hamburger size are stable after init (widths are cached once).
 */
export default function priorityPlusNav() {
    return {
        _ul: null,
        _items: [],
        _gap: 0,
        _hamburger: null,
        _hamburgerWidth: 0,
        _observer: null,

        init() {
            const nav = this.$el.querySelector('.nav-default');
            this._ul = nav?.querySelector('ul');
            this._hamburger = document.querySelector('.js-open-mobile-menu');

            if (!this._ul || !this._hamburger) return;

            // Measure the hamburger's rendered footprint once by briefly making it visible.
            this._hamburger.classList.remove('hidden');
            const btnStyle = getComputedStyle(this._hamburger);
            this._hamburgerWidth = this._hamburger.offsetWidth
                + (parseFloat(btnStyle.marginLeft) || 0)
                + (parseFloat(btnStyle.marginRight) || 0);
            this._hamburger.classList.add('hidden');

            // Cache the natural width of every <li> before any hiding occurs.
            // getBoundingClientRect gives sub-pixel accuracy.
            this._items = Array.from(
                this._ul.querySelectorAll(':scope > li')
            ).map(el => ({ el, width: el.getBoundingClientRect().width }));

            // Parse the flex gap between items so we include it in width sums.
            this._gap = parseFloat(getComputedStyle(this._ul).columnGap) || 0;

            this._observer = new ResizeObserver(() => this._recalculate());
            this._observer.observe(this.$el);

            this._recalculate();

            // Reveal the nav now that items are correctly configured.
            // Must happen after _recalculate() so the correct items are already
            // hidden before the element becomes visible.
            this.$el.removeAttribute('data-nav-pending');
        },

        destroy() {
            this._observer?.disconnect();
        },

        // ── Core measurement ─────────────────────────────────────────────────────

        _recalculate() {
            const count = this._items.length;
            if (count === 0) return;

            const containerWidth = this.$el.clientWidth;

            // Total width of all items including the gaps between them.
            const totalItemsWidth = this._items.reduce(
                (sum, item, i) => sum + item.width + (i > 0 ? this._gap : 0),
                0
            );

            if (totalItemsWidth <= containerWidth) {
                // Everything fits — restore all items and hide the hamburger.
                this._items.forEach(({ el }) => el.classList.remove('hidden'));
                this._hamburger.classList.add('hidden');
                return;
            }

            // Items overflow. Show the hamburger and find how many fit alongside it.
            this._hamburger.classList.remove('hidden');
            const available = containerWidth - this._hamburgerWidth;

            let runningWidth = 0;
            let splitIndex = count; // fallback: all items visible (loop always breaks in overflow path)

            for (let i = 0; i < count; i++) {
                const w = this._items[i].width + (i > 0 ? this._gap : 0);
                if (runningWidth + w > available) {
                    splitIndex = i;
                    break;
                }
                runningWidth += w;
            }

            // Enforce a minimum of 3 visible items. If the split would leave
            // fewer than that, go hamburger-only instead of showing a partial set.
            const MIN_VISIBLE = 3;
            if (splitIndex < MIN_VISIBLE) splitIndex = 0;

            // Show items before the split; hide items from the split onward.
            this._items.forEach(({ el }, i) => {
                el.classList.toggle('hidden', i >= splitIndex);
            });
        },
    };
}
